import React from 'react';

/**
 * What an open job card shows while the job itself is on its way.
 *
 * <p>Opening a card fetches the job, its work items and its resources. Until that
 * answered, the card showed the little it had been handed by the board — a name and a
 * number — with empty sections under it, so a job with four work items opened looking
 * like a job with none. The shapes here stand in for the tiles, the description and
 * the work-item rows, so the card reads as loading rather than as empty.
 *
 * <p>🔑 Driven by {@code detailLoading}, which is the FETCH — never by
 * {@code operationLoading}, which is every write on an open card. Saving a work item
 * must not blank the card the user is working in.
 *
 * Presentational only.
 *
 * @param {number} [rows=3] work-item rows to stand in for
 */
export default function JobDetailLoading({ rows = 3, className = '' }) {
    return (
        <div role="status" aria-label="Loading the job" data-testid="job-detail-loading"
             className={`animate-pulse ${className}`}>
            {/* The tiles: customer, cost, odometer. */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {Array.from({ length: 3 }, (_, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg px-2.5 py-1.5">
                        <div className="h-2.5 w-16 rounded bg-gray-200 mb-2" />
                        <div className="h-3.5 w-24 rounded bg-gray-100" />
                    </div>
                ))}
            </div>

            {/* The description block. */}
            <div className="mt-4 h-16 w-full rounded-xl bg-gray-100" />

            {/* The work items, which is what the card is mostly about. */}
            <div className="mt-4 space-y-3">
                {Array.from({ length: rows }, (_, i) => (
                    <div key={i} className="rounded-xl bg-gray-50 p-3">
                        <div className="flex items-center justify-between mb-3">
                            <div className="h-3.5 w-40 rounded bg-gray-200" />
                            <div className="h-3.5 w-16 rounded bg-gray-100" />
                        </div>
                        <div className="h-3 w-full rounded bg-gray-100" />
                    </div>
                ))}
            </div>
        </div>
    );
}
